import { TableCell, TableRow, IconButton, Tooltip, CircularProgress } from '@mui/material';
import { createUseStyles } from '@/services/createUseStyles';
import { Ellipsis } from '@/components/Ellipsis';
import ReactMarkdown from 'react-markdown';
import { Asset } from '@/api/generatedApi';
import { AnalysisFieldIcon, AnalysisFieldInfo, AnalysisFieldState } from './types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useMetadataSave } from '../../hooks/api/assets/useMetadataSave';

const useStyles = createUseStyles(theme => ({
  AIAnalysisSidebar_tableRow: {
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  },
  AIAnalysisSidebar_tableCell: {
    padding: theme.spacing(1, 2),
    border: 'none',
    backgroundColor: theme.palette.background.default,
    overflow: 'hidden',
    verticalAlign: 'top',
  },
  AIAnalysisSidebar_tableCellLabel: {
    fontWeight: 'bold',
    width: '160px',
    borderTopLeftRadius: theme.shape.borderRadius,
    borderBottomLeftRadius: theme.shape.borderRadius,
    whiteSpace: 'nowrap',
  },
  AIAnalysisSidebar_tableCellValue: {
    width: '55%',
    '& p': {
      margin: 0,
    },
    '& ul, & ol': {
      margin: 0,
      paddingLeft: theme.spacing(2),
    },
    '& pre': {
      backgroundColor: theme.palette.background.surface,
      padding: theme.spacing(1),
      borderRadius: theme.shape.borderRadius,
      overflowX: 'auto',
    },
    '& code': {
      backgroundColor: theme.palette.background.surface,
      padding: theme.spacing(0.5),
      borderRadius: theme.shape.borderRadius,
    },
  },
  AIAnalysisSidebar_tableCellAction: {
    width: '50px',
    textAlign: 'center',
    padding: theme.spacing(1),
  },
}));

interface AIAnalysisFieldRowProperties {
  fieldKey: string;
  value: string;
  asset?: Asset;
}

const getIconComponent = (icon: AnalysisFieldIcon) => {
  switch (icon) {
    case AnalysisFieldIcon.CHECK: {
      return <CheckCircleIcon fontSize='small' color='success' />;
    }
    case AnalysisFieldIcon.EDIT: {
      return <EditIcon fontSize='small' />;
    }
    case AnalysisFieldIcon.SAVE: {
      return <SaveIcon fontSize='small' />;
    }
  }
};

const getAnalysisFieldInfo = (
  key: string,
  value: string,
  asset: Asset | undefined,
  isUpdating: boolean,
  isCreating: boolean,
  promotingKey: string | undefined
): AnalysisFieldInfo => {
  const fieldExists = asset?.metadata?.[key] !== undefined;
  const valueMatches = fieldExists && asset?.metadata?.[key] === value;
  const isProcessing = isUpdating || isCreating || promotingKey === key;

  if (valueMatches) {
    return {
      state: AnalysisFieldState.MATCHES,
      tooltip: 'Metadata already matches',
      icon: AnalysisFieldIcon.CHECK,
      disabled: true,
    };
  }

  if (fieldExists) {
    return {
      state: AnalysisFieldState.EXISTS,
      tooltip: 'Update metadata',
      icon: AnalysisFieldIcon.EDIT,
      disabled: isProcessing,
    };
  }

  return {
    state: AnalysisFieldState.NEW,
    tooltip: 'Save to metadata',
    icon: AnalysisFieldIcon.SAVE,
    disabled: isProcessing,
  };
};

export const AdvancedAnalysisFieldRow = ({ fieldKey, value, asset }: AIAnalysisFieldRowProperties) => {
  const c = useStyles();
  const { saveAsMetadata, isUpdating, isCreating, promotingKey } = useMetadataSave(asset);

  const fieldInfo = getAnalysisFieldInfo(fieldKey, value, asset, isUpdating, isCreating, promotingKey);

  return (
    <TableRow className={c.AIAnalysisSidebar_tableRow}>
      <TableCell className={`${c.AIAnalysisSidebar_tableCell} ${c.AIAnalysisSidebar_tableCellLabel}`}>
        <Ellipsis text={fieldKey} />
      </TableCell>
      <TableCell className={`${c.AIAnalysisSidebar_tableCell} ${c.AIAnalysisSidebar_tableCellValue}`}>
        <ReactMarkdown>{value}</ReactMarkdown>
      </TableCell>
      <TableCell className={`${c.AIAnalysisSidebar_tableCell} ${c.AIAnalysisSidebar_tableCellAction}`}>
        <Tooltip title={fieldInfo.tooltip}>
          <span>
            <IconButton size='small' onClick={() => void saveAsMetadata(fieldKey, value)} disabled={fieldInfo.disabled}>
              {promotingKey === fieldKey ? <CircularProgress size={20} /> : getIconComponent(fieldInfo.icon)}
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};
